/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import { Filter, getFilterElementsDefault, useWorkplaceState } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addBrand,
    changeBrand,
    deleteBrand,
    getBrand,
    getBrandsExcel,
    getBrandsList,
} from 'axios/brandApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { Brand, BrandAdd, BrandChange, BrandTable } from 'models/api/brand';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { checkboxFormatter, deleteActionBtnFormatter } from 'utils/columnFormatters';
import BrandAddButton from './BrandAddButton';
import BrandsChangeModal from './BrandChangeModal';
import BrandFilter from './BrandsFilter';

const Brands = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { hasRole } = useAuth();

    return (
        <BOWorkplace<BrandTable, Brand, Filter<Omit<Brand, 'props'>>, BrandChange, BrandAdd>
            testId="brands"
            pageTitle="BackOfficeBrands"
            filterParams={{
                id: '',
                name: '',
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    formatter: checkboxFormatter('isActive'),
                },
                { key: 'code', name: <Trans i18nKey="BackOfficeCode" /> },
                { key: 'name', name: <Trans i18nKey="BackOfficeName" /> },
                { key: 'tariffs', name: <Trans i18nKey="BackOfficeTariffs" /> },
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('brandsDelete')),
                },
            ]}
            storagePath="brands/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeId') },
                { id: 'name', name: t('BackOfficeName') },
            ])}
            showModal={hasRole('brandsGetById') ? () => setIsModalVisible(true) : undefined}
            buttons={
                hasRole('brandsCreate')
                    ? [<BrandAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            filter={<BrandFilter />}
            modal={{
                component: isModalVisible && (
                    <BrandsChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getBrand(api, id),
                changeMutationFn: (item) => changeBrand(api, item),
                addMutationFn: (item) => addBrand(api, item),
            }}
            deleteMutationFn={(id) => deleteBrand(api, id)}
            fetchQueryFn={(filter) => getBrandsList(api, filter)}
            queryKey={['brands']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getBrandsExcel(api, filter)}
            showExcelBtn={hasRole('brandsExcel')}
        />
    );
};

const useBrandWorkplaceState = () => useWorkplaceState<BrandTable, Brand, BrandChange, BrandAdd>();

export default Brands;
export { useBrandWorkplaceState };
